import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { DEFAULT_TIER, TIER_INFO, TIER_ORDER_EASY_TO_HARD, getTierInfo } from '../../lib/learningTiers';
import DailyPracticeReportPanel from '../../components/DailyPracticeReportPanel';
import AssignPracticeModal from '../../components/AssignPracticeModal';
import StudentPracticeAssignmentsModal from '../../components/StudentPracticeAssignmentsModal';
import AssignWordsToStudentModal from '../../components/AssignWordsToStudentModal';
import StudentVocabularyModeManager from '../../components/StudentVocabularyModeManager';
import AdminStudentIeltsQuizList from '../../components/AdminStudentIeltsQuizList';
import AdminStudentAdaptiveReadingList from '../../components/AdminStudentAdaptiveReadingList';
import { useI18n } from '../../i18n/I18nProvider';
import { ClipboardDocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  countGroupedLearnedWords,
  formatLearnedDate,
  formatLearnedWordList,
  groupLearnedWordsByDate,
} from '../../lib/learnedWordExport';

const todayLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const api = async (method, url, body=null) => {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    method,
    headers: { 'Authorization': `Bearer ${token}`, ...(body?{'Content-Type':'application/json'}:{}) },
    body: body ? JSON.stringify(body) : null
  });
  const data = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(data?.message || 'Request failed');
  return data;
};

const StudentManagePage = () => {
  const { t, locale } = useI18n();
  const { studentId } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search || '');
  const back = params.get('back');
  const classIdFromBack = (back || '').match(/\/admin\/class\/([a-f0-9]{24})/i)?.[1] || '';
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState(DEFAULT_TIER);
  const [saving, setSaving] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignPracticeModalOpen, setAssignPracticeModalOpen] = useState(false);
  const [quizAssignmentsOpen, setQuizAssignmentsOpen] = useState(false);
  const [assignPracticeJob, setAssignPracticeJob] = useState(null);
  const [openDates, setOpenDates] = useState(new Set());
  // Remove "Create Secret Wordbook" entry; keep pick-from-box only
  const [showPickModal, setShowPickModal] = useState(false);
  const [boxes, setBoxes] = useState([]);
  const [goal, setGoal] = useState(0);
  // Daily detail modal (replaces inline expand)
  const [showDayDetailOf, setShowDayDetailOf] = useState('');
  const [dayDetail, setDayDetail] = useState(null);
  // Sentence reordering error log: toggle time/word columns (hidden by default)
  const [showReorderMeta, setShowReorderMeta] = useState(false);
  const [expandedStudySession, setExpandedStudySession] = useState('');
  const [reportDate, setReportDate] = useState(todayLocal);
  const [dailyReport, setDailyReport] = useState(null);
  const [dailyReportLoading, setDailyReportLoading] = useState(true);
  const [dailyReportError, setDailyReportError] = useState('');
  const [rawCopyState, setRawCopyState] = useState('idle');
  const [showLearnedWords, setShowLearnedWords] = useState(false);
  const [learnedCopyState, setLearnedCopyState] = useState('idle');
  const toggleOpen = (d) => {
    setOpenDates(prev => { const n = new Set(prev); if (n.has(d)) n.delete(d); else n.add(d); return n; });
  };

  const openDayDetail = (dateStr) => {
    try {
      setShowDayDetailOf(dateStr);
      const list = (info?.study_by_day || []);
      const found = list.find(d => d.date === dateStr) || null;
      setDayDetail(found);
    } catch (e) {
      setDayDetail(null);
    }
  };

  const closeDayDetail = () => {
    setShowDayDetailOf('');
    setDayDetail(null);
  };

  const learnedWordGroups = groupLearnedWordsByDate(info?.study_by_day || []);
  const learnedWordCount = countGroupedLearnedWords(learnedWordGroups);

  const copyAllLearnedWords = async () => {
    const text = formatLearnedWordList(learnedWordGroups, locale);
    if (!text) return;
    setLearnedCopyState('copying');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      setLearnedCopyState('copied');
      window.setTimeout(() => setLearnedCopyState('idle'), 2200);
    } catch (_) {
      setLearnedCopyState('failed');
      window.setTimeout(() => setLearnedCopyState('idle'), 3000);
    }
  };


  const load = async ({ showPageLoading = true } = {}) => {
    if (showPageLoading) setLoading(true);
    setError('');
    try {
      const data = await api('GET', `/api/admin/students/${encodeURIComponent(studentId)}/overview`);
      setInfo(data);
      setTier(data?.minimum_tier || data?.tier || DEFAULT_TIER);
      setGoal(Number(data?.learning_goal || 0));
    } catch (e) { if (showPageLoading) setError(e.message); }
    finally { if (showPageLoading) setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable react-hooks/exhaustive-deps */ }, [studentId]);

  useEffect(() => {
    if (!classIdFromBack || !studentId) return undefined;
    let cancelled = false;
    api(
      'GET',
      `/api/admin/classes/${classIdFromBack}/students/${studentId}/adaptive-reading-assignment-jobs/active`,
    )
      .then((data) => {
        if (!cancelled && data?.job) setAssignPracticeJob(data.job);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [classIdFromBack, studentId]);

  useEffect(() => {
    const jobId = assignPracticeJob?.id;
    if (!classIdFromBack || !studentId || !jobId) return undefined;
    if (!['queued', 'running'].includes(assignPracticeJob?.status)) return undefined;
    let cancelled = false;
    let timer;

    const poll = async () => {
      try {
        const data = await api(
          'GET',
          `/api/admin/classes/${classIdFromBack}/students/${studentId}/adaptive-reading-assignment-jobs/${jobId}`,
        );
        if (cancelled || !data?.job) return;
        setAssignPracticeJob(data.job);
        if (data.job.status === 'completed') {
          setNotice(t('Reading practice assigned: {name}', {
            name: data.job.assignment?.name || t('Reading practice'),
          }));
          window.setTimeout(() => setNotice(''), 5000);
          load();
          return;
        }
        if (data.job.status === 'failed') {
          setNotice(t(data.job.message || 'The practice could not be assigned. Please try again.'));
          window.setTimeout(() => setNotice(''), 6000);
          return;
        }
      } catch (_) {
        // A temporary polling failure should not hide an active preparation.
      }
      if (!cancelled) timer = window.setTimeout(poll, 1200);
    };

    timer = window.setTimeout(poll, 500);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // load is intentionally omitted; polling is keyed to the persisted job id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignPracticeJob?.id, assignPracticeJob?.status, classIdFromBack, studentId, t]);

  useEffect(() => {
    let cancelled = false;
    const loadDailyReport = async () => {
      setDailyReportLoading(true);
      setDailyReportError('');
      setRawCopyState('idle');
      try {
        const query = new URLSearchParams({ date: reportDate });
        if (classIdFromBack) query.set('class_id', classIdFromBack);
        const data = await api('GET', `/api/admin/students/${encodeURIComponent(studentId)}/daily-practice-report?${query}`);
        if (!cancelled) setDailyReport(data);
      } catch (reportError) {
        if (!cancelled) setDailyReportError(reportError.message || 'Failed to load daily report');
      } finally {
        if (!cancelled) setDailyReportLoading(false);
      }
    };
    loadDailyReport();
    return () => { cancelled = true; };
  }, [studentId, reportDate, classIdFromBack]);

  const copyRawDailyLogs = async () => {
    setRawCopyState('copying');
    try {
      const query = new URLSearchParams({ date: reportDate, include_raw: '1' });
      if (classIdFromBack) query.set('class_id', classIdFromBack);
      const data = await api('GET', `/api/admin/students/${encodeURIComponent(studentId)}/daily-practice-report?${query}`);
      const text = JSON.stringify(data.raw_export || {}, null, 2);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      setRawCopyState('copied');
      window.setTimeout(() => setRawCopyState('idle'), 2200);
    } catch (copyError) {
      setRawCopyState('idle');
      alert(copyError.message || 'Failed to copy raw logs');
    }
  };

  const saveTier = async () => {
    try { setSaving(true); await api('PUT', `/api/admin/students/${studentId}/tier`, { tier }); await load(); }
    catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  const saveGoal = async () => {
    try {
      setSaving(true);
      const n = Math.max(0, Math.min(500, parseInt(goal, 10) || 0));
      await api('PUT', `/api/admin/students/${studentId}/learning-goal`, { goal: n });
      await load();
      alert('Learning goal updated');
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  // 已移除创建秘制词库的功能

  const openPick = async () => {
    try {
      setShowPickModal(true);
      const data = await api('GET', '/api/admin/secret-boxes');
      setBoxes(Array.isArray(data) ? data : []);
    } catch (e) { alert(e.message); }
  };

  const pickFromBox = async (boxId) => {
    try {
      setSaving(true);
      await api('POST', `/api/admin/students/${studentId}/secret-wordbook-from-box`, { box_id: boxId });
      setShowPickModal(false);
      await load();
      alert('Set from wordbook box');
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  const student = info?.student || {};

  return (
    <div>
    <div className="space-y-6">
      {notice && (
        <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-green-800">
          {notice}
        </div>
      )}
      
      <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">Student Management</h2>
              {back && (
                <Link to={back} className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">Back to Class</Link>
              )}
              {classIdFromBack && (
                <>
                  <button
                    type="button"
                    onClick={() => setAssignModalOpen(true)}
                    className="px-3 py-2 bg-green-600 text-white rounded text-sm font-semibold hover:bg-green-700"
                  >
                  {t('Assign words')}
                  </button>
                  <button
                    type="button"
                    disabled={['queued', 'running'].includes(assignPracticeJob?.status)}
                    onClick={() => setAssignPracticeModalOpen(true)}
                    className="px-3 py-2 bg-indigo-600 text-white rounded text-sm font-semibold hover:bg-indigo-700 disabled:cursor-wait disabled:bg-indigo-400"
                  >
                    {['queued', 'running'].includes(assignPracticeJob?.status) ? (
                      <span className="flex flex-col items-start leading-tight">
                        <span>
                          {t('Assigning...')} {assignPracticeJob?.progress?.completed || 0}/6
                        </span>
                        <span className="mt-0.5 text-[11px] font-normal text-indigo-50">
                          {t(assignPracticeJob?.progress?.message || 'Preparing the first draft')}
                        </span>
                      </span>
                    ) : t('Assign practice')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuizAssignmentsOpen(true)}
                    className="px-3 py-2 border border-indigo-200 text-indigo-700 bg-white rounded text-sm font-semibold hover:bg-indigo-50"
                  >
                    {t('Quiz list')}
                  </button>
                </>
              )}
            </div>
            <div data-i18n-skip className="mt-1 text-gray-600">{student.username} {student.nickname ? `(${student.nickname})` : ''}</div>
          </div>
          {info?.secret_wordbook_title && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Secret Wordbook:</span>
              <span className="px-2 py-1 rounded bg-purple-50 text-purple-700 border border-purple-200 text-sm">{info.secret_wordbook_title}</span>
              <button onClick={openPick} className="px-3 py-2 bg-purple-600 text-white rounded">Edit</button>
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Minimum Learning Level</label>
          <select value={tier} onChange={e=>setTier(e.target.value)} className="p-2 border rounded bg-gray-50">
            {TIER_ORDER_EASY_TO_HARD.map((tierId) => (
              <option key={tierId} value={tierId}>{TIER_INFO[tierId].teacherLabel}</option>
            ))}
          </select>
          <button onClick={saveTier} disabled={saving} className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50">Save</button>
          <span className="basis-full text-sm text-gray-500 md:basis-auto md:max-w-md">
            {getTierInfo(tier).teacherDescription} Students may choose this level or any more challenging level.
          </span>
          <span className="basis-full text-sm text-gray-500 md:basis-auto">
            Current default: {getTierInfo(info?.default_tier || tier).studentLabel}
          </span>
          <div className="h-6 w-px bg-gray-200 mx-2" />
          <label className="text-sm text-gray-600">Daily Learning Goal</label>
          <input type="number" min={0} max={500} value={goal} onChange={e=>setGoal(e.target.value)} className="w-24 p-2 border rounded bg-gray-50" />
          <button onClick={saveGoal} disabled={saving} className="px-3 py-2 bg-green-600 text-white rounded disabled:opacity-50">Update Goal</button>
          {!info?.secret_wordbook_title && (
            <>
              <button onClick={openPick} className="px-3 py-2 bg-purple-50 text-purple-700 rounded border border-purple-200">Choose from Secret Wordbook Box</button>
              <a href="/admin/secret-box" className="px-3 py-2 bg-gray-100 text-gray-700 rounded border">Manage Secret Wordbook Box</a>
            </>
          )}
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
        <h4 className="font-bold text-gray-900 mb-2">{t('Adaptive reading records')}</h4>
        <AdminStudentAdaptiveReadingList classId={classIdFromBack} studentId={studentId} />
        <div className="mt-7 border-t border-gray-200 pt-6">
          <h5 className="mb-1 text-sm font-semibold text-gray-800">{t('IELTS speaking quizzes')}</h5>
          <AdminStudentIeltsQuizList classId={classIdFromBack} studentId={studentId} />
        </div>
      </div>

      <StudentVocabularyModeManager
        classId={classIdFromBack}
        studentId={studentId}
        overview={info}
        onSaved={() => load({ showPageLoading: false })}
      />

      <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h4 className="font-bold text-gray-900">{t('Daily Review')}</h4>
            {info?.review_plan?.completion_reason === 'no_review_due' ? (
              <p className="text-sm text-gray-600 mt-1">{t('No review due today')}</p>
            ) : (
              <p className="text-sm text-gray-600 mt-1">
                {t('{done}/{goal} meanings completed', {
                  done: info?.review_plan?.completed_today || 0,
                  goal: info?.review_plan?.required_today || 0,
                })} · {t('{count} meanings in review queue', { count: info?.review_plan?.due_count || 0 })}
              </p>
            )}
          </div>
          <span className={`px-3 py-1 rounded text-sm font-semibold ${info?.review_plan?.review_complete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
            {info?.review_plan?.completion_reason === 'no_review_due' ? t('No review due today') :
              info?.review_plan?.completion_reason === 'all_due_completed' ? t('All due reviews completed') :
                info?.review_plan?.completion_reason === 'target_met' ? t('Daily review goal met') :
                  info?.review_plan?.completed_today ? t('In progress') : t('Not started')}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {(info?.review_today || []).map((w, i)=>(<span key={i} className="px-2 py-1 bg-gray-50 border rounded text-sm">{w}</span>))}
        </div>
      </div>

      <DailyPracticeReportPanel
        report={dailyReport}
        date={reportDate}
        onDateChange={setReportDate}
        loading={dailyReportLoading}
        error={dailyReportError}
        studentName={student.nickname || student.username}
        sessions={info?.study_sessions || []}
        expandedSession={expandedStudySession}
        onExpandedSessionChange={setExpandedStudySession}
        onCopyRaw={copyRawDailyLogs}
        copyState={rawCopyState}
      />

      {false && <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h4 className="font-bold text-gray-900">Study Sessions</h4>
            <p className="mt-1 text-sm text-gray-500">Difficulty, meanings, and four-step practice activity.</p>
          </div>
          <span className="text-sm font-medium text-gray-600">{info?.study_sessions?.length || 0} recorded</span>
        </div>
        {Array.isArray(info?.study_sessions) && info.study_sessions.length > 0 ? (
          <div className="space-y-3">
            {info.study_sessions.map((session) => {
              const expanded = expandedStudySession === session.session_id;
              const tierInfo = getTierInfo(session.tier);
              const events = Array.isArray(session.events) ? session.events : [];
              const stageNames = {
                1: 'Infer meaning',
                2: 'Definition check',
                3: 'Sentence reordering',
                4: 'Meaning match',
              };
              return (
                <div key={session.session_id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedStudySession(expanded ? '' : session.session_id)}
                    className="w-full p-4 text-left hover:bg-gray-50"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-gray-900">
                          {session.mode === 'review' ? 'Review' : 'Learning'} · {tierInfo.studentLabel}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {session.started_at ? new Date(session.started_at).toLocaleString() : 'Unknown time'}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className={`rounded px-2 py-1 font-medium ${session.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                          {session.status || 'started'}
                        </span>
                        <span className="rounded bg-blue-50 px-2 py-1 text-blue-800">{session.item_count || 0} meanings</span>
                        <span className="rounded bg-gray-100 px-2 py-1 text-gray-700">
                          {session.correct_attempts || 0}/{session.attempts_total || 0} correct attempts
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(session.items || []).map((item) => (
                        <span key={`${item.word}::${item.sense_id}`} data-i18n-skip className="rounded border bg-white px-2 py-1 text-xs text-gray-700">
                          <strong>{item.word}</strong>{item.definition_cn ? ` · ${item.definition_cn}` : ''}
                        </span>
                      ))}
                    </div>
                  </button>
                  {expanded && (
                    <div className="border-t bg-gray-50 p-4">
                      {events.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-left text-sm">
                            <thead className="text-xs uppercase text-gray-500">
                              <tr><th className="p-2">Time</th><th className="p-2">Word / meaning</th><th className="p-2">Step</th><th className="p-2">Result</th><th className="p-2">Support</th></tr>
                            </thead>
                            <tbody className="divide-y bg-white">
                              {events.map((event, index) => {
                                const item = (session.items || []).find(candidate => candidate.word === event.word && candidate.sense_id === event.sense_id);
                                return (
                                  <tr key={`${event.ts || index}-${index}`}>
                                    <td className="p-2 whitespace-nowrap text-xs text-gray-500">{event.ts ? new Date(event.ts).toLocaleTimeString() : ''}</td>
                                    <td data-i18n-skip className="p-2"><strong>{event.word}</strong>{item?.definition_cn ? <div className="text-xs text-gray-500">{item.definition_cn}</div> : null}</td>
                                    <td className="p-2">{stageNames[event.stage] || `Step ${event.stage}`}</td>
                                    <td className="p-2">{event.correct ? <span className="font-medium text-green-700">Correct</span> : <span className="font-medium text-red-700">Retry</span>}</td>
                                    <td className="p-2 text-xs text-gray-600">{event.used_hint ? 'Chinese hint' : event.alternative_reviewed ? (event.alternative_accepted ? 'AI accepted alternative' : 'AI reviewed') : 'None'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : <div className="text-sm text-gray-500">Session opened; no four-step attempts recorded yet.</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : <div className="text-sm text-gray-500">No structured study sessions recorded yet. New sessions will appear here.</div>}
      </div>}

      <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-bold text-gray-900">Daily Study Log (last 30 days)</h4>
          <button
            type="button"
            onClick={() => {
              setLearnedCopyState('idle');
              setShowLearnedWords(true);
            }}
            className="inline-flex items-center gap-2 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={learnedWordCount === 0}
          >
            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
            View all learned words
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 text-gray-600">
              <tr><th className="p-2">Date</th><th className="p-2">Learned</th><th className="p-2">Reviewed</th><th className="p-2">Review Done</th><th className="p-2">Actions</th></tr>
            </thead>
            <tbody className="divide-y">
              {[...(info?.study_by_day || [])].reverse().map((d)=> (
                <React.Fragment key={d.date}>
                  <tr key={d.date} className="hover:bg-gray-50">
                    <td className="p-2">{d.date}</td>
                    <td className="p-2">{d.learned}</td>
                    <td className="p-2">{d.reviewed}</td>
                    <td className="p-2">
                      {d.review_done ? (
                        <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">Done</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs">Pending</span>
                      )}
                    </td>
                    <td className="p-2">
                      <button onClick={()=>openDayDetail(d.date)} className="px-3 py-1 bg-indigo-600 text-white rounded text-sm">View Details</button>
                    </td>
                  </tr>
                  
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showLearnedWords && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 p-3 sm:p-5">
          <div className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">All learned words</h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t('{count} unique words across {days} learning days', {
                    count: learnedWordCount,
                    days: learnedWordGroups.length,
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowLearnedWords(false)}
                className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                title={t('Close')}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-4 sm:px-6">
              <div className="space-y-5">
                {learnedWordGroups.map((group) => (
                  <section key={group.date}>
                    <div className="mb-2 flex items-center gap-3">
                      <h4 className="font-semibold text-gray-900">
                        {formatLearnedDate(group.date, locale)}
                      </h4>
                      <span className="text-xs text-gray-500">
                        {t('{count} words', { count: group.words.length })}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-x-6 gap-y-1 border-l-2 border-indigo-100 pl-4 sm:grid-cols-2 lg:grid-cols-3">
                      {group.words.map((word) => (
                        <div key={`${group.date}-${word}`} data-i18n-skip className="break-words py-1 text-sm text-gray-800">
                          {word}
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-gray-50 px-5 py-4 sm:px-6">
              <p className="text-sm text-gray-600">Words are grouped by their first recorded learning date.</p>
              <button
                type="button"
                onClick={copyAllLearnedWords}
                disabled={learnedCopyState === 'copying' || learnedWordCount === 0}
                className="inline-flex items-center gap-2 rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                {learnedCopyState === 'copied' ? t('Copied') :
                  learnedCopyState === 'failed' ? t('Copy failed') :
                    learnedCopyState === 'copying' ? t('Copying...') : t('Copy dated list')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-bold text-gray-900">Sentence Reordering Error Log ({Array.isArray(info?.reordering_error_logs) ? info.reordering_error_logs.length : 0})</h4>
          <button
            onClick={() => setShowReorderMeta(v => !v)}
            className="px-3 py-1.5 rounded text-sm border bg-gray-50 hover:bg-gray-100 text-gray-700"
          >
            {showReorderMeta ? 'Hide Time/Word' : 'Show Time/Word'}
          </button>
        </div>
        {Array.isArray(info?.reordering_error_logs) && info.reordering_error_logs.length > 0 ? (
          <div className="max-h-80 overflow-y-auto border rounded">
            <table className={`min-w-full text-left ${!showReorderMeta ? 'table-fixed' : ''}`}>
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {showReorderMeta && (<th className="p-2">Time</th>)}
                  {showReorderMeta && (<th className="p-2">Word</th>)}
                  <th className={`p-2 ${!showReorderMeta ? 'w-1/3' : ''}`}>Student Answer</th>
                  <th className={`p-2 ${!showReorderMeta ? 'w-1/3' : ''}`}>Correct Answer</th>
                  <th className={`p-2 ${!showReorderMeta ? 'w-1/3' : ''}`}>Explanation</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {info.reordering_error_logs.map((e, idx) => (
                  <tr key={idx} className="align-top">
                    {showReorderMeta && (<td className="p-2 text-xs text-gray-500 whitespace-nowrap">{e.ts}</td>)}
                    {showReorderMeta && (<td data-i18n-skip className="p-2 text-sm text-gray-800">{e.word}</td>)}
                    <td data-i18n-skip className={`p-2 text-sm text-gray-800 whitespace-pre-wrap ${!showReorderMeta ? 'w-1/3' : ''}`}>{(e.user_answer || '').replaceAll('_',' ')}</td>
                    <td data-i18n-skip className={`p-2 text-sm text-blue-700 whitespace-pre-wrap ${!showReorderMeta ? 'w-1/3' : ''}`}>{(e.correct_answer || '').replaceAll('_',' ')}</td>
                    <td data-i18n-skip className={`p-2 text-sm text-gray-700 whitespace-pre-wrap ${!showReorderMeta ? 'w-1/3' : 'max-w-[400px]'}`}>{e.explanation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-gray-500">No records</div>
        )}
      </div>
    </div>
    {/* Remove create-secret modal; keep pick-from-box only */}
    {classIdFromBack && (
      <>
        <AssignWordsToStudentModal
          isOpen={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          classId={classIdFromBack}
          student={{ ...student, _id: student._id || studentId }}
          onAssigned={(message) => {
            setAssignModalOpen(false);
            setNotice(message || 'Assigned');
            window.setTimeout(() => setNotice(''), 3000);
            load();
          }}
        />
        <AssignPracticeModal
          isOpen={assignPracticeModalOpen}
          onClose={() => setAssignPracticeModalOpen(false)}
          classId={classIdFromBack}
          student={{ ...student, _id: student._id || studentId }}
          onAssigned={(message) => {
            setAssignPracticeModalOpen(false);
            setNotice(message || 'Reading practice assigned');
            window.setTimeout(() => setNotice(''), 5000);
            load();
          }}
        />
        <StudentPracticeAssignmentsModal
          isOpen={quizAssignmentsOpen}
          onClose={() => setQuizAssignmentsOpen(false)}
          classId={classIdFromBack}
          student={{ ...student, _id: student._id || studentId }}
          onChanged={load}
        />
      </>
    )}
    {showPickModal && (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 relative">
          <button className="absolute top-3 right-3 text-2xl leading-none text-gray-500 hover:text-gray-800" onClick={()=>setShowPickModal(false)}>×</button>
          <h3 className="text-xl font-bold mb-2 text-gray-800">Choose from Secret Wordbook Box</h3>
          <div className="max-h-80 overflow-y-auto">
            {boxes.length === 0 && <div className="text-gray-600">No wordbooks. Please create one first.</div>}
            {boxes.map(b => (
              <div key={b._id} className="flex items-center justify-between border-b py-2">
                <div>
                  <div data-i18n-skip className="font-medium text-gray-900">{b.title}</div>
                  <div className="text-xs text-gray-600">{b.count} words</div>
                </div>
                <button className="px-3 py-1.5 bg-purple-600 text-white rounded" onClick={()=>pickFromBox(b._id)}>Select</button>
              </div>
            ))}
          </div>
          <div className="mt-4 text-right">
            <button className="px-4 py-2 bg-gray-200 rounded" onClick={()=>setShowPickModal(false)}>Close</button>
          </div>
        </div>
      </div>
    )}
    {Boolean(showDayDetailOf) && (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl p-6 relative">
          <button className="absolute top-3 right-3 text-2xl leading-none text-gray-500 hover:text-gray-800" onClick={closeDayDetail}>×</button>
          <h3 className="text-xl font-bold mb-4 text-gray-800">{showDayDetailOf} Study Details</h3>
          {!dayDetail ? (
            <div className="text-gray-600">No data</div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              <div>
                <div className="text-sm text-gray-700 mb-2">Learned in Secret Wordbook ({Array.isArray(dayDetail.secret_learned_words) ? dayDetail.secret_learned_words.length : 0})</div>
                {Array.isArray(dayDetail.secret_learned_words) && dayDetail.secret_learned_words.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {dayDetail.secret_learned_words.map((w, i) => (
                      <span key={`sl-${i}`} className="px-2 py-1 bg-gray-50 border rounded text-sm">{w}</span>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">(None)</div>
                )}
              </div>
              <div>
                <div className="text-sm text-gray-700 mb-2">Other Learned Words ({Array.isArray(dayDetail.other_learned_words) ? dayDetail.other_learned_words.length : 0})</div>
                {Array.isArray(dayDetail.other_learned_words) && dayDetail.other_learned_words.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {dayDetail.other_learned_words.map((w, i) => (
                      <span key={`ol-${i}`} className="px-2 py-1 bg-gray-50 border rounded text-sm">{w}</span>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">(None)</div>
                )}
              </div>
              <div>
                <div className="text-sm text-gray-700 mb-2">Words Reviewed Today ({Array.isArray(dayDetail.reviewed_words) ? dayDetail.reviewed_words.length : 0})</div>
                {Array.isArray(dayDetail.reviewed_words) && dayDetail.reviewed_words.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {dayDetail.reviewed_words.map((w, i) => (
                      <span key={`rv-${i}`} className="px-2 py-1 bg-gray-50 border rounded text-sm">{w}</span>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">(None)</div>
                )}
              </div>
            </div>
          )}
          <div className="mt-4 text-right">
            <button className="px-4 py-2 bg-gray-200 rounded" onClick={closeDayDetail}>Close</button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default StudentManagePage;
